import { PayloadAction } from '@reduxjs/toolkit';

import { DesignState } from 'editor/src/store/design/types';
import { isReflowMember } from 'editor/src/store/design/util/reflowMember';

interface ElementYUpdate {
  uuid: number;
  y?: number;
  x?: number;
  y1?: number;
  y2?: number;
  x1?: number;
  x2?: number;
}

interface RowShiftUpdate {
  /** See ReflowRowShiftUpdate in computeReflowState.ts for why groupId scopes
   *  the update — without it, two groups carrying rows with identical ids
   *  would collide. */
  groupId: string;
  rowId: string;
  shiftY?: number;
  elementShiftsX?: Record<string, number>;
}

/**
 * Applies the position part of the reflow state (the hide part flows through
 * `updateHiddenElementsAction`). Mutates `element.y` for the given uuids and
 * `row.shiftY` for the given row ids on the target spread.
 *
 * Do NOT dispatch this directly — go through `updateHiddenElementsOperation`
 * so the hide channel and the position channel stay in sync.
 */
const applyReflowStateReducer = (
  state: DesignState,
  action: PayloadAction<{
    spreadIndex: number;
    elementUpdates: ElementYUpdate[];
    rowShiftUpdates: RowShiftUpdate[];
  }>,
) => {
  if (!state.designData) {
    return;
  }
  const { spreadIndex, elementUpdates, rowShiftUpdates } = action.payload;
  const spread = state.designData.spreads[spreadIndex];
  if (!spread) {
    return;
  }

  if (elementUpdates.length) {
    const updatesByUuid = new Map<number, ElementYUpdate>();
    // Merged, not replaced: computeReflowState emits a row's vertical shift
    // ({uuid, y}) and its horizontal packing ({uuid, x}) as separate entries,
    // so the same element can arrive twice in one pass and both must apply.
    elementUpdates.forEach((u) => updatesByUuid.set(u.uuid, { ...updatesByUuid.get(u.uuid), ...u }));
    spread.pages.forEach((page) => {
      page.groups.media?.forEach((element) => {
        if (!isReflowMember(element)) {
          return;
        }
        const update = updatesByUuid.get(element.uuid);
        if (!update) {
          return;
        }
        if (element.type === 'line') {
          if (update.y1 !== undefined) {
            element.y1 = update.y1;
          }
          if (update.y2 !== undefined) {
            element.y2 = update.y2;
          }
          if (update.x1 !== undefined) {
            element.x1 = update.x1;
          }
          if (update.x2 !== undefined) {
            element.x2 = update.x2;
          }
        } else {
          if (update.y !== undefined) {
            element.y = update.y;
          }
          if (update.x !== undefined) {
            element.x = update.x;
          }
        }
      });
    });
  }

  if (rowShiftUpdates.length && spread.reflowGroups) {
    // Key by `${groupId}::${rowId}` so two groups holding rows with the same
    // rowId do not collide. The "::" separator is fine because row ids are
    // generated alphanumerically (no "::" in them).
    const keyOf = (groupId: string, rowId: string): string => `${groupId}::${rowId}`;
    const updatesByCompositeKey = new Map<string, RowShiftUpdate>();
    rowShiftUpdates.forEach((u) => updatesByCompositeKey.set(keyOf(u.groupId, u.rowId), u));
    spread.reflowGroups.forEach((group) => {
      group.rows.forEach((row) => {
        const update = updatesByCompositeKey.get(keyOf(group.id, row.id));
        if (!update) {
          return;
        }
        if (update.shiftY !== undefined) {
          row.shiftY = update.shiftY === 0 ? undefined : update.shiftY;
        }
        if (update.elementShiftsX !== undefined) {
          const allZero = Object.values(update.elementShiftsX).every((v) => v === 0);
          row.elementShiftsX = allZero ? undefined : update.elementShiftsX;
        }
      });
    });
  }
};

export default applyReflowStateReducer;
